import Layout from '@/components/layouts/Layout';
import LayoutExplore from '@/components/layouts/LayoutExplore';
import ProtectedLayout from '@/components/layouts/ProtectedLayout';
import { QueryBoundary } from '@/components/shared/QueryBoundary';
import AcceptInvite from '@/features/auth/AcceptInvite';
import Login from '@/features/auth/Login';
import PasswordResetConfirm from '@/features/auth/PasswordResetConfirm';
import PasswordResetRequest from '@/features/auth/PasswordResetRequest';
import Signup from '@/features/auth/Signup';
import SignupComplete from '@/features/auth/SignupComplete';
import Subscribe from '@/features/onboarding/Subscribe';
import { toContentHref } from '@/utils/content-ref';
import { useEffect, useState } from 'react';
import {
  Navigate,
  Outlet,
  useParams,
} from 'react-router-dom';
import { DetailsContent } from './pages/details/DetailsContent';
import { Calendar } from './pages/calendar/Calendar';
import { HubAdmin } from './pages/settings/HubAdmin';
import { Settings } from './pages/settings/Settings';
import { Work4Me } from './pages/work4me/Work4Me';
import { useUser } from '@/hooks';
import { EXTERNAL_CONNECT_COMPLETION_PATH } from '@vivport/backend/types';

// Version component to display in bottom left corner
function VersionDisplay() {
  const [version, setVersion] = useState<string>('');

  useEffect(() => {
    fetch('/version.json')
      .then(response => response.json())
      .then(data => setVersion(data.version))
      .catch(() => setVersion('unknown'));
  }, []);

  if (!version) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        bottom: '8px',
        left: '8px',
        fontSize: '12px',
        color: 'hsl(var(--muted-foreground))',
        zIndex: 1000,
        fontFamily: 'monospace',
        backgroundColor: 'hsl(var(--background) / 0.8)',
        padding: '2px 6px',
        borderRadius: '4px',
        border: '1px solid hsl(var(--border))'
      }}
    >
      v{version}
    </div>
  );
}

function App() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Outlet />
      <VersionDisplay />
    </>
  );
}

function LegacyExploreContentRedirect() {
  const { id } = useParams();
  if (!id) {
    return <Navigate to="/explore" replace />;
  }
  return <Navigate to={toContentHref(id)} replace />;
}

function AdminGate() {
  const memberContextQuery = useUser();

  return (
    <QueryBoundary query={memberContextQuery} errorMessage="Failed to load member context">
      {memberContextQuery.data?.isHubAdmin ? (
        <Outlet />
      ) : (
        <Navigate to="/explore" replace />
      )}
    </QueryBoundary>
  );
}

export const appRoutes = [
  {
    path: '/',
    element: <App />,
    children: [
      // Public routes
      { path: 'login', element: <Login /> },
      { path: 'signup', element: <Signup /> },
      { path: 'signup/complete', element: <SignupComplete /> },
      { path: 'accept-invite', element: <AcceptInvite /> },
      { path: 'reset-password', element: <PasswordResetRequest /> },
      { path: 'reset-password/confirm', element: <PasswordResetConfirm /> },
      
      // Protected routes group
      {
        element: <ProtectedLayout />,
        children: [
          { path: 'subscribe', element: <Subscribe /> },
          {
            path: 'explore',
            element: <Layout key="explore-layout" />,
            children: [{ index: true, element: <LayoutExplore /> }]
          },
          {
            path: 'explore/:id',
            element: <LegacyExploreContentRedirect />
          },
          {
            path: 'settings',
            element: <Layout key="settings-layout" />,
            children: [
              { index: true, element: <Navigate to="/settings/general" replace /> },
              { path: 'general', element: <Settings /> },
              // Absolute paths derived from the backend constant: the parent's full
              // path is '/settings', so these cannot drift from the redirect target
              // the backend builds for external-connect completion.
              { path: EXTERNAL_CONNECT_COMPLETION_PATH, element: <Settings /> },
              { path: `${EXTERNAL_CONNECT_COMPLETION_PATH}/:kind/:id`, element: <Settings /> },
              { path: 'billing', element: <Settings /> },
              { path: 'usage', element: <Settings /> },
              { path: 'hubs', element: <Settings /> },
              { path: 'neo', element: <Settings /> },
              { path: 'mcp', element: <Navigate to="/settings/integrations" replace /> },
              { path: '*', element: <Navigate to="/settings/general" replace /> },
            ]
          },
          {
            path: 'calendar',
            element: <Layout key="calendar-layout" />,
            children: [{ index: true, element: <Calendar /> }]
          },
          {
            path: 'work4me',
            element: <Layout key="work4me-layout" />,
            children: [
              { index: true, element: <Navigate to="/work4me/agents" replace /> },
              { path: ':section/:id', element: <Work4Me /> },
              { path: ':section', element: <Work4Me /> },
            ]
          },
          { path: 'agents', element: <Navigate to="/work4me/agents" replace /> },
          { path: 'tabs', element: <Navigate to="/work4me/tags" replace /> },
          { path: 'tags', element: <Navigate to="/work4me/tags" replace /> },
          { path: 'filters', element: <Navigate to="/work4me/filters" replace /> },
          {
            path: 'content/:contentRef',
            element: <Layout key="content-layout" />,
            children: [{ index: true, element: <DetailsContent /> }]
          },
          {
            path: 'admin',
            element: <Layout key="admin-layout" />,
            children: [{
              element: <AdminGate />,
              children: [
                { index: true, element: <Navigate to="/admin/members" replace /> },
                { path: ':section', element: <HubAdmin /> },
              ]
            }]
          }
        ]
      },
      
      // Default redirects
      { path: '/', element: <Navigate to="/explore" replace /> },
      { path: '*', element: <Navigate to="/explore" replace /> }
    ]
  }
];

export default App;
